<template>
  <v-dialog v-model="viewDialog" class="the-app-dialogs" persistent :max-width="hasSaleDetail ? '1100px' : '800px'">
    <v-card>
      <v-layout row class="no-select not-printable" style="background-color:#09132f">
        <h3 id="dialog-title" class="text-uppercase px-3 pt-3" style="text-xs-center letter-spacing:2px">
          <v-layout align-center>
            <v-icon light x-large class="rgba(0,0,0,0.3)--text pb-1">attach_money</v-icon>
            <span class="px-3">{{ $t('View Receivable Statement Information') }}</span>
          </v-layout>
        </h3>
        <v-spacer />
        <v-card-actions>
          <v-btn fab icon dark @click="$emit('hidemodal')">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-actions>
      </v-layout>

      <v-card-title class="view-dialog-content grey lighten-3" id="printArea">
        <invoice-header class="only-for-print" />

        <v-layout column>
          <table class="section-title-table only-for-print">
            <tr>
              <th class="section-title-content">
                {{ $t('Receivable Statement') }}
              </th>
            </tr>
          </table>

          <v-layout row wrap class="invoice-content">
            <v-flex xs12 sm12 :class="{ 'px-4': $vuetify.breakpoint.smAndUp }">
              <v-layout row wrap>
                <table class="info-table" style="width: 100%">
                  <tr>
                    <td class="td-padding-bottom">
                      <div class="caption no-select">{{ $t('Customer') }}</div>
                      <strong>{{ viewInfo.customer_name || '-' }}</strong>
                    </td>
                    <td class="td-padding-bottom">
                      <div class="caption no-select">{{ $t('Code') }}</div>
                      <strong>
                        {{ viewInfo.receivable_statement_auto_number || '-' }}
                      </strong>
                    </td>
                    <td class="td-padding-bottom">
                      <div class="caption no-select">{{ $t('Date') }}</div>
                      <strong>{{ viewInfo.statement_date }}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td class="td-padding-bottom">
                      <div class="caption no-select">{{ `${$t('Amount')} (${systemCurrency})` }}</div>
                      <strong>{{ viewInfo.amount | currency }}</strong>
                    </td>
                    <td class="td-padding-bottom">
                      <div class="caption no-select">{{ `${$t('Balance')} (${systemCurrency})` }}</div>
                      <strong>{{ viewInfo.balance | currency }}</strong>
                    </td>
                    <td class="td-padding-bottom">
                      <div class="caption no-select">{{ `${$t('Received')} (${systemCurrency})` }}</div>
                      <strong>{{ viewInfo.received | currency }}</strong>
                    </td>
                  </tr>
                  <tfoot>
                    <tr class="only-for-print">
                      <td>
                        <div style="height: 50px;">&nbsp;</div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </v-layout>

              <v-layout row wrap justify-center mt-3 v-if="viewInfo.remittance_details">
                <table>
                  <tr>
                    <th class="section-title-content">
                      {{ $t('Remittance Details') }}
                    </th>
                  </tr>
                </table>

                <v-flex xs10>
                  <v-divider />
                </v-flex>
              </v-layout>

              <v-layout row wrap class="invoice-content" mt-4 v-if="viewInfo.remittance_details">
                <v-flex xs12 sm12>
                  <v-layout row wrap>
                    <table class="printed-table" cellspacing="0">
                      <thead>
                        <tr>
                          <th xs1 class="pa-2 view-table-header">#</th>
                          <th class="view-table-header" xs3 pa-2>
                            {{ $t('Remittance Office') }}
                          </th>
                          <th class="view-table-header" xs3 pa-2>
                            {{ $t('Date') }}
                          </th>
                          <th class="view-table-header" xs2 pa-2>
                            {{ `${$t('Commission')} (${systemCurrency})` }}
                          </th>
                          <th class="view-table-header" xs2 pa-2>
                            {{ `${$t('Amount')} (${systemCurrency})` }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, index) in 1" :key="index">
                          <td class="view-table-row">
                            <p class="view-table-content">{{ index + 1 }}</p>
                          </td>
                          <td class="view-table-row">
                            <p class="view-table-content">
                              {{ viewInfo.remittance_details.remittance_office_name }}
                            </p>
                          </td>
                          <td class="view-table-row">
                            <p class="view-table-content">
                              {{ viewInfo.remittance_details.transaction_date }}
                            </p>
                          </td>
                          <td class="view-table-row">
                            <p class="view-table-content">
                              {{ viewInfo.remittance_details.remittance_commission | currency }}
                            </p>
                          </td>
                          <td class="view-table-row">
                            <p class="view-table-content">
                              {{ viewInfo.remittance_details.amount | currency }}
                            </p>
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr class="only-for-print">
                          <td>
                            <div class="footer-space">&nbsp;</div>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </v-layout>
                </v-flex>
              </v-layout>

              <v-layout row wrap justify-center mt-4 v-if="hasSaleDetail">
                <table>
                  <tr>
                    <th class="section-title-content">
                      {{ $t('Sale Details') }}
                    </th>
                  </tr>
                </table>

                <v-flex xs10>
                  <v-divider />
                </v-flex>
              </v-layout>

              <v-layout row wrap class="invoice-content" mt-4 v-if="hasSaleDetail">
                <v-flex xs12 sm12>
                  <v-layout row wrap>
                    <table class="info-table" style="width: 100%">
                      <tr>
                        <td class="td-padding-bottom">
                          <div class="caption no-select">
                            {{ $t('Invoice Number') }}
                          </div>
                          <strong>{{ saleDetail.bill_auto_number || '-' }}</strong>
                        </td>
                        <td class="td-padding-bottom">
                          <div class="caption no-select">
                            {{ $t('Customer Name') }}
                          </div>
                          <strong>{{ saleDetail.customer_name || '-' }}</strong>
                        </td>
                        <td class="td-padding-bottom">
                          <div class="caption no-select">
                            {{ $t('Bill Date') }}
                          </div>
                          <strong v-if="saleDetail.bill_date">
                            {{ saleDetail.bill_date }}
                          </strong>
                          <strong v-else>-</strong>
                        </td>
                      </tr>
                    </table>

                    <table class="printed-table" cellspacing="0">
                      <thead>
                        <tr class="only-for-print">
                          <td>
                            <div class="header-space">&nbsp;</div>
                          </td>
                        </tr>
                        <tr>
                          <th xs1 class="pa-2 view-table-header">#</th>
                          <th class="view-table-header" xs3 pa-2>
                            {{ $t('Product') }}
                          </th>
                          <th class="view-table-header" xs2 pa-2>
                            {{ $t('Quantity') }}
                          </th>
                          <th class="view-table-header" xs2 pa-2>
                            {{ $t('Returned Quantity') }}
                          </th>
                          <th class="view-table-header" xs2 pa-2>
                            {{ `${$t('Price')} (${systemCurrency})` }}
                          </th>
                          <th class="view-table-header" xs2 pa-2>
                            {{ `${$t('Total')} (${systemCurrency})` }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, index) in rowNumbers" :key="index">
                          <td class="view-table-row">
                            <p class="view-table-content">{{ index + 1 }}</p>
                          </td>
                          <td class="view-table-row">
                            <p class="view-table-content">
                              {{ saleDetail.product_name[index] }}
                            </p>
                          </td>
                          <td class="view-table-row">
                            <p class="view-table-content">
                              {{ saleDetail.sale_quantity[index] | currency }}
                            </p>
                          </td>
                          <td class="view-table-row">
                            <p class="view-table-content">
                              {{ saleDetail.returned_quantity[index] | currency }}
                            </p>
                          </td>
                          <td class="view-table-row">
                            <p class="view-table-content">
                              {{ saleDetail.sale_price[index] | currency }}
                            </p>
                          </td>
                          <td class="view-table-row">
                            <p class="view-table-content">
                              {{ saleDetail.total[index] | currency }}
                            </p>
                          </td>
                        </tr>
                        <tr class="last-table-row-for-currency" v-if="saleDetail.discount_amount > 0">
                          <td class="last-table-row" colspan="5">
                            <p class="view-table-content">
                              {{ `${$t('Total Amount')} (${systemCurrency})` }}
                            </p>
                          </td>
                          <td class="last-table-row2">
                            <p class="view-table-content">
                              {{ saleDetail.total_amount | currency }}
                            </p>
                          </td>
                        </tr>
                        <tr class="last-table-row-for-currency" v-if="saleDetail.discount_amount > 0">
                          <td class="last-table-row" colspan="5">
                            <p class="view-table-content">
                              {{ `${$t('Discount Amount')} (${systemCurrency})` }}
                            </p>
                          </td>
                          <td class="last-table-row2">
                            <p class="view-table-content">
                              {{ saleDetail.discount_amount | currency }}
                            </p>
                          </td>
                        </tr>
                        <tr class="last-table-row-for-currency">
                          <td class="last-table-row" colspan="5">
                            <p class="view-table-content">
                              {{ `${$t('Net Amount')} (${systemCurrency})` }}
                            </p>
                          </td>
                          <td class="last-table-row2">
                            <p class="view-table-content">
                              {{ saleDetail.net_amount | currency }}
                            </p>
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr class="only-for-print">
                          <td>
                            <div style="height: 50px;">&nbsp;</div>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </v-layout>
                </v-flex>
              </v-layout>

              <v-layout row wrap justify-center mt-4 v-if="receiveNumbers > 0">
                <table>
                  <tr>
                    <th class="section-title-content">
                      {{ $t('Receive Details') }}
                    </th>
                  </tr>
                </table>

                <v-flex xs10>
                  <v-divider />
                </v-flex>
              </v-layout>

              <v-layout row wrap class="invoice-content" mt-4 v-if="receiveNumbers > 0">
                <v-flex xs12 sm12>
                  <v-layout row wrap>
                    <table class="printed-table" cellspacing="0">
                      <thead>
                        <tr>
                          <th xs1 class="pa-2 view-table-header">#</th>
                          <th class="view-table-header" xs3 pa-2>
                            {{ $t('Date') }}
                          </th>
                          <th class="view-table-header" xs2 pa-2>
                            {{ `${$t('Amount')} (${systemCurrency})` }}
                          </th>
                          <th class="view-table-header" xs2 pa-2>
                            {{ `${$t('Amount')} (${systemCurrency === $t('USD') ? $t('IQD') : $t('USD')})` }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, index) in receiveNumbers" :key="index">
                          <td class="view-table-row">
                            <p class="view-table-content">{{ index + 1 }}</p>
                          </td>
                          <td class="view-table-row">
                            <p class="view-table-content">
                              {{ receiveDetail.date[index] }}
                            </p>
                          </td>
                          <td class="view-table-row">
                            <p class="view-table-content">
                              {{ receiveDetail.amount_main[index] | currency }}
                            </p>
                          </td>
                          <td class="view-table-row">
                            <p class="view-table-content">
                              {{ receiveDetail.amount_secondary[index] | currency }}
                            </p>
                          </td>
                        </tr>
                        <tr class="last-table-row-for-currency">
                          <td class="last-table-row" colspan="2">
                            <p class="view-table-content">
                              {{ `${$t('Total Received')}` }}
                            </p>
                          </td>
                          <td class="last-table-row2" colspan="2">
                            <p class="view-table-content">
                              {{ viewInfo.received | currency }}
                            </p>
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr class="only-for-print">
                          <td>
                            <div class="footer-space">&nbsp;</div>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </v-layout>
                </v-flex>
              </v-layout>

              <invoice-footer
                :showSignatureSection="true"
                :accountantName="viewInfo.accountant"
                :payerRecipientName="viewInfo.customer_name"
                :payerOrRecipient="$t('Payer')"
                class="only-for-print"
              />
            </v-flex>
          </v-layout>
        </v-layout>
      </v-card-title>
      <v-divider light />

      <v-card-actions class="grey lighten-3 not-printable">
        <v-layout justify-end>
          <v-btn outline color="primary" @click="$emit('hidemodal')">
            {{ $t('Close') }}
          </v-btn>
          <v-btn color="primary" @click="printMe()">
            <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">print</v-icon>
            {{ $t('Print') }}
          </v-btn>
        </v-layout>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import ignored from 'print-this'

export default {
  name: 'view-component',

  components: {
    invoiceHeader: () => import('@/views/_partials/invoiceHeader.vue'),
    invoiceFooter: () => import('@/views/_partials/invoiceFooter.vue')
  },

  props: {
    viewDialog: { type: Boolean, required: true }
  },

  data() {
    return {
      systemCurrency: localStorage.systemCurrency,
      rowNumbers: 0,
      hasSaleDetail: false,
      saleDetail: {
        purchase_id: 0,
        bill_auto_number: '',
        vender_name: '',
        bill_date: '',
        receive_type: '',
        total_amount: '',
        discount_amount: '',
        net_amount: '',
        product_name: [],
        sale_quantity: [],
        returned_quantity: [],
        sale_price: [],
        total: []
      },
      receiveNumbers: 0,
      hasReceiveDetail: false,
      receiveDetail: {
        date: [],
        amount_main: [],
        amount_secondary: []
      }
    }
  },

  watch: {
    viewDialog: {
      immediate: false,
      handler() {
        if (this.viewDialog) {
          this.hasSaleDetail = this.viewInfo.detail !== null
          this.saleDetail.product_name = []
          this.saleDetail.sale_quantity = []
          this.saleDetail.returned_quantity = []
          this.saleDetail.sale_price = []
          this.saleDetail.total = []

          if (this.hasSaleDetail) {
            this.rowNumbers = this.viewInfo.detail.data_detail.length
            this.saleDetail.bill_auto_number = this.viewInfo.detail.data.bill_auto_number
            this.saleDetail.customer_name = this.viewInfo.detail.data.customer_name
            this.saleDetail.bill_date = this.viewInfo.detail.data.bill_date

            this.saleDetail.total_amount = this.viewInfo.detail.data.total_amount
            this.saleDetail.discount_amount = this.viewInfo.detail.data.discount_amount
            this.saleDetail.net_amount = this.viewInfo.detail.data.net_amount
            this.saleDetail.sale_id = this.viewInfo.detail.data.purchase_id
            this.viewInfo.detail.data_detail.forEach(c => {
              this.saleDetail.product_name.push(c.product_name)
              this.saleDetail.sale_quantity.push(c.sale_quantity)
              this.saleDetail.returned_quantity.push(c.returned_quantity)
              this.saleDetail.sale_price.push(c.sale_price)
              this.saleDetail.total.push(c.total)
            })
          }

          this.hasReceiveDetail = this.viewInfo.receive_detail !== null
          this.receiveDetail.date = []
          this.receiveDetail.amount_main = []
          this.receiveDetail.amount_secondary = []
          if (this.hasReceiveDetail) {
            this.receiveNumbers = this.viewInfo.receive_detail.length
            this.viewInfo.receive_detail.forEach(c => {
              this.receiveDetail.date.push(c.receive_date)
              this.receiveDetail.amount_main.push(c.amount_main)
              this.receiveDetail.amount_secondary.push(c.amount_secondary)
            })
          }
        }
      }
    }
  },

  computed: {
    ...mapGetters({
      baseUrls: 'misc/getBaseUrls',
      viewInfo: 'receivableStatements/getStatementInfo'
    })
  },

  updated() {
    document.addEventListener('keyup', e => {
      // if the "ESC" button was pressed and this dialog is shown, hide it
      if (e.keyCode === 27 && this.viewDialog) {
        this.$emit('hidemodal')
      }
    })
  },

  methods: {
    printMe() {
      // we pass true parameter to the function
      // in this way we know that we print invoice and some other style should apply
      // if we don't pass anything it will be false which means
      // we don't print invoice and other styles won't be applied
      // helpers.doPrinting(this.hasSaleDetail)
      let onlyForPrint = document.querySelectorAll('.only-for-print')
      let notPrintable = document.querySelectorAll('.not-printable')
      let invoiceContent = document.querySelector('.invoice-content')
      let theDialogContent = document.querySelector('.view-dialog-content')
      let infoTable = document.querySelector('.info-table')
      infoTable.classList.add('add-padding-for-info-table')
      theDialogContent.classList.remove('grey')
      invoiceContent.style.marginTop = '90px'
      notPrintable.forEach(element => {
        element.style.display = 'none'
      })
      onlyForPrint.forEach(element => {
        element.style.display = 'block'
      })
      if (this.hasSaleDetail) {
        document.querySelector('.printed-table').style.marginTop = '-120px'
      }

      this.$htmlToPaper('printArea', () => {
        infoTable.classList.remove('add-padding-for-info-table')
        theDialogContent.classList.add('grey')
        invoiceContent.style.marginTop = '0px'
        onlyForPrint.forEach(element => {
          element.style.display = 'none'
        })
        notPrintable.forEach(element => {
          element.style.display = 'flex'
        })
        if (this.hasSaleDetail) {
          document.querySelector('.printed-table').style.marginTop = '0px'
        }
      })
    }
  }
}
</script>

<style scoped>
.only-for-print {
  display: none;
}
</style>
